import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field, Form } from 'react-final-form';

import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@material-ui/core/TextField';
import { makeStyles, createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
//import LogoAGN from '../AGN_logo_128.svg';
import LogoAGN from '../AGN_logo_def2.svg';
import GuiVersion from '../version';
import OpenInNew from '@material-ui/icons/OpenInNew';

import { Notification, useTranslate, useLogin, useNotify } from 'react-admin';
import isTest, { apiPath } from './isTest';
import appTheme, { testColor } from './theme';

const useStyles = makeStyles(theme => ({
    main: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        alignItems: 'center',
        justifyContent: 'flex-start',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%',
        backgroundColor: isTest && testColor?testColor:theme.palette.primary.main,
    },
    card: {
        minWidth: 300,
        marginTop: '6em',
    },
    avatar: {
        margin: '1em',
        display: 'flex',
        justifyContent: 'center',
    },
    icon: {
        backgroundColor: theme.palette.secondary.main,
    },
    hint: {
        marginTop: '1em',
        display: 'flex',
        justifyContent: 'center',
        color: theme.palette.grey[500],
    },
    form: {
        padding: '0 1em 1em 1em',
        // links wie im legacy-system: fett, ohne unterstrich, bei hover unterstrichen
        '& a': {
            color: theme.palette.primary.main,
            fontWeight: 'bold',
            textDecoration: 'none',
        },
        '& a:hover': {
            textDecoration: 'underline',
        },
    },
    footer: {
        position: 'fixed',
        right: '1em',
        bottom: '0.5em',
        fontSize: '0.8em',
        color: theme.palette.grey[500],
        '& a': {
            color: 'inherit',
            textDecoration: 'none',
        },
        '& a:hover': {
            textDecoration: 'underline',
        },
        '& svg': {
            fontSize: '1em',
            verticalAlign: 'middle',
            marginLeft: '0.2em',
        },
    },
    input: {
        marginTop: '1em',
    },
    actions: {
        padding: '0 1em 1em 1em',
    },
}));

const renderInput = ({
    meta: { touched, error } = {},
    input: { ...inputProps },
    ...props
}) => (
    <TextField
        error={!!(touched && error)}
        helperText={touched && error}
        {...inputProps}
        {...props}
        inputProps={props.inputProps || {} /* ueberschreibt data-lpignore aus dem theme: lastpass soll das login ausfuellen */}
        fullWidth
    />
);

const Login = ({ location }) => {
    const [loading, setLoading] = useState(false);
    const [clicked, setClicked] = useState(false);
    const [done, setDone] = useState(false);
    const [apiVersion, setApiVersion] = useState('');

    // api-version fuer die fusszeile: der header Touren-Server kommt bei jeder antwort, auch bei OPTIONS ohne login
    useEffect(() => {
        fetch(apiPath+'/authToken', { method: 'OPTIONS' })
            .then(response => {
                const server = JSON.parse(response.headers.get('Touren-Server') || '{}');
                setApiVersion(server.api || '');
            })
            .catch(() => {});
    }, []);

    const translate = useTranslate();
    const classes = useStyles();
    const notify = useNotify();
    const login = useLogin();
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const resetkey = params.get('resetkey');
    const recoveryMode = resetkey ? 'reset' : (params.get('forgotPassword') ? 'request' : false);

    const handleSubmit = auth => {
        setLoading(true);
        setClicked(true);
        login(auth, location.state ? location.state.nextPathname : '/')
            .then(() => setLoading(false))
            .catch(error => {
                setLoading(false);
                notify(
                    typeof error === 'string'
                        ? error
                        : typeof error === 'undefined' || !error.message
                        ? 'ra.auth.sign_in_error'
                        : error.message,
                    'warning'
                );
            });
    };

    const handlePasswordRecovery = values => {
        setLoading(true);
        const isReset = recoveryMode==='reset';
        const data = isReset
            ? {resetkey: resetkey, password: values.password, password2: values.password2}
            : {username: values.username};
        fetch(apiPath+'/authPasswordRecovery', {
            method: isReset ? 'PUT' : 'POST',
            headers: new Headers({ 'Content-Type': 'application/json' }),
            body: JSON.stringify(data),
        })
            .then(response => response.json().then(data => ({ok: response.ok, data: data})))
            .then(result => {
                if (!result.ok) {
                    throw new Error(result.data.message);
                }
                setDone(true);
                setLoading(false);
            })
            .catch(error => {
                setLoading(false);
                notify(error.message || 'Die Anfrage konnte nicht verarbeitet werden.', 'warning');
            });
    };

    const validate = values => {
        const errors = {};
        if (recoveryMode==='request' && !values.username) {
            errors.username = translate('ra.validation.required');
        }
        if (recoveryMode==='reset') {
            if (!values.password) {
                errors.password = translate('ra.validation.required');
            }
            if (values.password !== values.password2) {
                errors.password2 = 'Die beiden Passwörter stimmen nicht überein.';
            }
        }
        if (recoveryMode) {
            return errors;
        }
        if (!values.username && !values.token) {
            errors.username = translate('ra.validation.required');
        }
        if (!values.password && !values.token) {
            errors.password = translate('ra.validation.required');
        }
        return errors;
    };
    
    var get = {};
    if (!clicked) {
      var parts = window.location.search.substr(1).split("&");
      for (var i = 0; i < parts.length; i++) {
        var temp = parts[i].split("=");
        get[decodeURIComponent(temp[0])] = decodeURIComponent(temp[1]);
      }
    }

    return (
        <Form
            onSubmit={recoveryMode ? handlePasswordRecovery : handleSubmit}
            validate={validate}
            render={({ handleSubmit }) => (
                <form onSubmit={handleSubmit} noValidate>
                    <div className={classes.main}>
                        <Card className={classes.card}>
                            <img src={LogoAGN} className={classes.logo} alt="Logo AGN" />
                            <div className={classes.form}>
                              {recoveryMode==='request' && !done ?
                                <div className={classes.input}>
                                  <h3>Passwort vergessen</h3>
                                  <Field
                                      autoFocus
                                      name="username"
                                      component={renderInput}
                                      label="Benutzername / E-Mail"
                                      disabled={loading}
                                  />
                                </div>
                              :null}
                              {recoveryMode==='reset' && !done ?
                                <div>
                                  <h3>Neues Passwort setzen</h3>
                                  <div className={classes.input}>
                                    <Field
                                        autoFocus
                                        name="password"
                                        component={renderInput}
                                        label="Neues Passwort"
                                        type="password"
                                        inputProps={{ autoComplete: 'new-password' }}
                                        disabled={loading}
                                    />
                                  </div>
                                  <div className={classes.input}>
                                    <Field
                                        name="password2"
                                        component={renderInput}
                                        label="Passwort wiederholen"
                                        type="password"
                                        inputProps={{ autoComplete: 'new-password' }}
                                        disabled={loading}
                                    />
                                  </div>
                                </div>
                              :null}
                              {done ?
                                <div>
                                  {recoveryMode==='reset'
                                    ? 'Das Passwort wurde geändert. Du kannst dich jetzt anmelden.'
                                    : 'Falls ein passender Benutzer vorhanden ist, wurde eine E-Mail zum Zurücksetzen des Passworts versendet.'}
                                </div>
                              :null}
                              {!recoveryMode && !loading?
                                <div className={classes.input}>
                                    <Field
                                        autoFocus
                                        name="username"
                                        component={renderInput}
                                        label={translate('ra.auth.username')}
                                        disabled={loading}
                                    />
                                </div>
                              :null}
                              {!recoveryMode && !loading?
                                <div className={classes.input}>
                                    <Field
                                        name="password"
                                        component={renderInput}
                                        label={translate('ra.auth.password')}
                                        type="password"
                                        disabled={loading}
                                    />
                                </div>
                              :null}
                            </div>
                            {!done ? <CardActions className={classes.actions}>
                                <Button
                                    variant="contained"
                                    type="submit"
                                    color="primary"
                                    disabled={loading}
                                    className={classes.button}
                                    fullWidth
                                >
                                    {loading && (
                                        <CircularProgress
                                            size={25}
                                            thickness={2}
                                        />
                                    )}
                                    {recoveryMode==='request'
                                      ? 'Link zum Zurücksetzen senden'
                                      : recoveryMode==='reset'
                                      ? 'Passwort ändern'
                                      : translate('ra.auth.sign_in')}
                                </Button>
                            </CardActions> : null}
                            <div className={classes.form}>
                              {!recoveryMode ?
                                <div><a href="#/login?forgotPassword=1">Passwort vergessen?</a></div>
                              :
                                <div><a href="#/login">zur&uuml;ck zum Login</a></div>
                              }
                            </div>
                        </Card>
                        <div className={classes.footer}>
                          | <a href="/index.php" target="_blank" rel="noopener noreferrer">vorgänger Version (2018)<OpenInNew /></a>
                          {' | gui'+GuiVersion}
                          {apiVersion ? ' | api'+apiVersion : ''}
                        </div>
                        <Notification />
                    </div>
                </form>
            )}
        />
    );
};

Login.propTypes = {
    authProvider: PropTypes.func,
    previousRoute: PropTypes.string,
};

// We need to put the ThemeProvider decoration in another component
// Because otherwise the useStyles() hook used in Login won't get
// the right theme
const LoginWithTheme = props => (
    <ThemeProvider theme={createMuiTheme(appTheme)}>
        <Login {...props} />
    </ThemeProvider>
);

export default LoginWithTheme;
